import { graphql } from 'gatsby';
import { useState, useMemo, useEffect } from 'react';
import ROIChart from './components/ROIChart/ROIChart';
import Link from '@/components/Link/Link';
import { applyPaddingCSS } from '@/utils/generatePadding';
import { type BlockHTMLId } from '@/utils/renderBlock';
import { type ROIChartArgs } from './components/ROIChart/ROIChart';
import { storeJSONInSession } from '@/utils/storage';
import {
  StyledCalculator,
  StyledChart,
  StyledLabel,
  StyledSelect,
  StyledInput,
  StyledLinkSection,
  StyledLinkSectionMobile,
  StyledInputSection,
  StyledContainer,
} from './ROICalculator.style';

export const defaultROIReportValues: ROIReportValues = {
  chartArgs: {
    title: null,
    xAxisLabel: '',
    timePeriods: 0,
    toolTip: '',
    values: [],
    hoursSaved: 0,
    hoursSavedLabel: '',
    costSaved: 0,
    costSavedLabel: '',
    perTimePeriodLabel: '',
    currency: 'usd',
  },
  chartInputs: {
    engineerCount: 0,
    engineerSalary: 0,
  },
  userInfo: {
    companyName: '',
    email: '',
  },
};

export type ROIReportValues = {
  chartArgs: ROIChartArgs;
  chartInputs: {
    engineerCount: number;
    engineerSalary: number;
  };
  userInfo: {
    companyName: string;
    email: string;
  };
};

// Configuration constants for ROI calculations
const ROI_CONFIG = {
  NUMBER_OF_WEEKS_IN_YEAR: 52,
  DEVELOPER_HOURS_PER_WEEK: 40,
  PERCENTAGE_OF_HOURS_SPENT_ON_BAD_CODE: 0.4, // 40% per Stripe survey
  SONAR_BENEFIT_PERCENTAGE: 0.25, // 25% of bad code problems addressed
  WORKING_WEEKS_PER_YEAR: 46,
  LABOR_COST_INFLATION_RATE: 1.03, // 3% annual inflation
} as const;
const DEFAULT_ENGINEER_COUNT = 75;

// Calculated constants
const HOURS_SPENT_ON_BAD_CODE_PER_WEEK =
  ROI_CONFIG.PERCENTAGE_OF_HOURS_SPENT_ON_BAD_CODE * ROI_CONFIG.DEVELOPER_HOURS_PER_WEEK;

const HOURS_SAVED_WITH_SONAR_PER_WEEK =
  ROI_CONFIG.SONAR_BENEFIT_PERCENTAGE * HOURS_SPENT_ON_BAD_CODE_PER_WEEK;

function calculateHourlyRate(annualSalary: number): number {
  return annualSalary / ROI_CONFIG.DEVELOPER_HOURS_PER_WEEK / ROI_CONFIG.NUMBER_OF_WEEKS_IN_YEAR;
}

function calculateAnnualHoursSaved(engineerCount: number): number {
  return HOURS_SAVED_WITH_SONAR_PER_WEEK * engineerCount * ROI_CONFIG.WORKING_WEEKS_PER_YEAR;
}

function calculateCumulativeROI(
  hoursSavedPerYear: number,
  hourlyRate: number,
  timePeriods: number
): number[] {
  return Array.from({ length: timePeriods }).reduce<number[]>((acc, _, year) => {
    const yearlyBenefit =
      hoursSavedPerYear * hourlyRate * Math.pow(ROI_CONFIG.LABOR_COST_INFLATION_RATE, year);
    const cumulativeValue = yearlyBenefit + (acc[year - 1] ?? 0);
    return [...acc, cumulativeValue];
  }, []);
}

function calculateROI(
  engineerCount: number,
  engineerSalary: number,
  timePeriods: number
): { values: number[]; hoursSaved: number; costSaved: number } {
  const hourlyRate = calculateHourlyRate(engineerSalary);
  const hoursSavedPerYear = calculateAnnualHoursSaved(engineerCount);
  const cumulativeValues = calculateCumulativeROI(hoursSavedPerYear, hourlyRate, timePeriods);

  const averageCostSavedPerYear = (cumulativeValues.at(-1) ?? 0) / timePeriods;

  return {
    values: cumulativeValues,
    hoursSaved: hoursSavedPerYear,
    costSaved: averageCostSavedPerYear,
  };
}

const ROICalculator = ({ system, elements }: Queries.ROI_CALCULATORFragment & BlockHTMLId) => {
  const {
    title,
    number_of_engineers_label,
    salary_dropdown_label,
    salary_options,
    x_axis_label,
    time_periods,
    tool_tip,
    hours_saved_label,
    cost_saved_label,
    per_time_period_label,
    currency,
    link,
    link_label,
    hours_saved_icon,
    cost_saved_icon,
  } = elements;

  const [engineerCount, setEngineerCount] = useState<number | string>(DEFAULT_ENGINEER_COUNT);
  const [engineerSalary, setEngineerSalary] = useState(
    salary_options.value[0]?.elements.value.value ?? 0
  );

  const numericEngineerCount = Number(engineerCount) || 0;

  const roi = useMemo(
    () => calculateROI(numericEngineerCount, engineerSalary, time_periods.value),
    [numericEngineerCount, engineerSalary, time_periods.value]
  );

  const handleEngineerCountKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-' || event.key === 'Subtract' || event.key === '.') {
      event.preventDefault();
    }
  };

  const handleEngineerCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Allow empty input
    if (value === '') {
      setEngineerCount('');
      return;
    }

    // Parse and validate input
    const numValue = Number.parseInt(value, 10);
    // Only update if valid positive integer
    if (!Number.isNaN(numValue) && numValue > 0) {
      setEngineerCount(numValue);
    }
  };

  const handleSalaryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setEngineerSalary(Number(event.target.value));
  };
  useEffect(() => {
    storeJSONInSession('ROI_CALCULATOR', {
      ...defaultROIReportValues,
      chartArgs: {
        title: title?.value ?? null,
        xAxisLabel: x_axis_label.value,
        timePeriods: time_periods.value,
        toolTip: tool_tip.value,
        values: roi.values,
        hoursSaved: roi.hoursSaved,
        hoursSavedLabel: hours_saved_label.value,
        costSaved: roi.costSaved,
        costSavedLabel: cost_saved_label.value,
        perTimePeriodLabel: per_time_period_label.value,
        currency: currency.value,
      },
      chartInputs: {
        engineerCount,
        engineerSalary,
      },
    });
  }, [
    roi,
    title?.value,
    x_axis_label.value,
    time_periods.value,
    tool_tip.value,
    hours_saved_label.value,
    cost_saved_label.value,
    per_time_period_label.value,
    currency.value,
    engineerCount,
    engineerSalary,
  ]);

  return (
    <StyledContainer data-codename={system.codename} css={applyPaddingCSS(elements)}>
      <StyledCalculator>
        <StyledInputSection>
          <StyledLabel>
            {number_of_engineers_label.value}
            <StyledInput
              type="number"
              min="1"
              value={engineerCount}
              onKeyDown={handleEngineerCountKeyDown}
              onChange={handleEngineerCountChange}
            />
          </StyledLabel>

          <StyledLabel>
            {salary_dropdown_label.value}
            <StyledSelect onChange={handleSalaryChange}>
              {salary_options.value.map((option) => (
                <option key={option.system.id} value={option.elements.value.value}>
                  {option.elements.label.value}
                </option>
              ))}
            </StyledSelect>
          </StyledLabel>

          {link?.value && (
            <StyledLinkSection>
              {link_label?.value && <StyledLabel>{link_label.value}</StyledLabel>}
              <Link
                {...link.value}
                variant="primary"
                disabled={!engineerCount || Number.parseInt(engineerCount.toString()) < 1}
              />
            </StyledLinkSection>
          )}
        </StyledInputSection>

        <StyledChart>
          <ROIChart
            title={title?.value}
            values={roi.values}
            hoursSaved={roi.hoursSaved}
            costSaved={roi.costSaved}
            xAxisLabel={x_axis_label.value}
            timePeriods={time_periods.value}
            toolTip={tool_tip.value}
            hoursSavedLabel={hours_saved_label.value}
            costSavedLabel={cost_saved_label.value}
            perTimePeriodLabel={per_time_period_label.value}
            currency={currency.value}
            hoursSavedIcon={hours_saved_icon}
            costSavedIcon={cost_saved_icon}
          />
        </StyledChart>

        {link?.value && (
          <StyledLinkSectionMobile>
            {link_label?.value && <StyledLabel>{link_label?.value}</StyledLabel>}
            <Link
              {...link.value}
              variant="primary"
              disabled={!engineerCount || Number.parseInt(engineerCount.toString()) < 1}
            />
          </StyledLinkSectionMobile>
        )}
      </StyledCalculator>
    </StyledContainer>
  );
};

export default ROICalculator;

export const fragments = graphql`
  fragment NUMBER_OPTION on kontent_item_number_option {
    __typename
    system {
      id
    }
    elements {
      label {
        value
      }
      value {
        value
      }
    }
  }

  fragment ROI_CALCULATOR on kontent_item_roi_calculator {
    __typename
    system {
      codename
      id
    }
    elements {
      ...PADDING
      title {
        value
      }
      number_of_engineers_label {
        value
      }
      salary_dropdown_label {
        value
      }
      salary_options {
        value {
          ...NUMBER_OPTION
        }
      }
      x_axis_label {
        value
      }
      time_periods {
        value
      }
      tool_tip {
        value
      }
      hours_saved_label {
        value
      }
      cost_saved_label {
        value
      }
      per_time_period_label {
        value
      }
      currency {
        value
      }
      link {
        value {
          ...LINK
        }
      }
      link_label {
        value
      }
      hours_saved_icon {
        value {
          url
        }
      }
      cost_saved_icon {
        value {
          url
        }
      }
    }
  }
`;
